<script>
import AkInputText from '@components/v2/input/AkInputText.vue';
import Checkbox from 'primevue/checkbox';
import AkShimmer from '@components/v2/general/AkShimmer.vue';

export default {
  name: 'GaFilterBaseAutocomplete',
  components: {AkShimmer, Checkbox, AkInputText},
  props: {
    filter: {
      type: Object,
      required: true,
    },
    filterProperty: {
      type: String,
      required: true,
    },
    searchMethod: {
      type: Function,
      required: true,
    },
    placeholder: {
      type: String,
      required: false,
    },
    minLength: {
      type: Number,
      default: 0,
    },
  },
  emits: ['update:filter', 'option-select'],
  data() {
    return {
      suggestions: [],
      suggestionsLoading: false,
      suggestionsSelected: [],
    };
  },
  computed: {
    searchFilter: {
      get() {
        return this.filter;
      },
      set(value) {
        this.$emit('update:filter', value);
      },
    },
    hasSuggestions() {
      return this.suggestionsComputed.length > 0;
    },
    suggestionsComputed() {
      return this.suggestions.filter((su) => !this.suggestionsSelected.includes(su.id));
    },
    hasTyped() {
      return this.searchFilter[this.filterProperty]?.length > this.minLength;
    },
  },
  methods: {
    autocomplete() {
      const len = this.searchFilter[this.filterProperty].trim().length;
      if (len < this.minLength) this.suggestions = [];
      else {
        this.suggestionsLoading = true;
        this.searchMethod(this.searchFilter).then((data) => {
          this.suggestions = data;
          this.suggestionsLoading = false;
        });
      }
    },
    resetSuggestions() {
      this.suggestions = [];
      this.suggestionsSelected = [];
    },
    initSelectedSuggestions(selectedSuggestions) {
      if (selectedSuggestions?.length > 0) this.suggestionsSelected = [...selectedSuggestions].map((su) => su.id);
    },
    removeSuggestion(option) {
      this.suggestionsSelected = this.suggestionsSelected.filter((id) => id !== option.id);
    },
    optionSelect(option) {
      this.suggestionsSelected.push(option.id);
      this.$emit('option-select', option);
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-1 px-3.5 py-3">
    <div class="px-5 flex items-center relative">
      <AkInputText
        inputClass="!bg-transparent !border-2 !border-grey-100 !rounded-full !pl-[40px] !pr-6"
        v-model="searchFilter[filterProperty]"
        @update:model-value="autocomplete"
        :placeholder="placeholder ?? 'Rechercher...'" />
      <i class="ga-icon ga-search text-3xl absolute left-8 grey-900" />
    </div>
    <!--  SUGGESTIONS  -->
    <div
      v-if="hasTyped"
      class="flex flex-col mx-5 bg-grey-50 rounded-md border border-grey-100 max-h-[200px] overflow-auto">
      <div
        v-if="hasSuggestions"
        v-for="option in suggestionsComputed"
        :key="option.id"
        class="py-2.5 px-3 w-full border-b last:border-b-0 hover:bg-grey-100">
        <label class="gap-3 flex items-center cursor-pointer">
          <checkbox
            v-model="suggestionsSelected"
            @update:model-value="optionSelect(option)"
            :value="option.id"
            class="[&_.p-checkbox-box]:!border-grey [&_.p-checkbox-box]:border [&_.p-checkbox-box]:rounded-6"
            :class="{
              '[&_.p-checkbox-box]:!bg-primary [&_.p-checkbox-box]:border-0': suggestionsSelected.includes(option.id),
            }" />
          <span class="font-medium text-grey-900 select-none">
            {{ option.display }}
          </span>
        </label>
      </div>
      <div v-else-if="suggestionsLoading" class="w-full flex flex-col gap-2 p-2 text-grey-400">
        <AkShimmer :loading="true" width="50%" />
        <AkShimmer :loading="true" width="35%" />
      </div>
      <div v-else class="w-full flex items-center justify-center h-[52px] p-2 text-grey-400">Aucun résultat</div>
    </div>
  </div>
</template>

<style scoped></style>
